import * as React from "react";

import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import { Typography } from "@material-ui/core";

import { IrisEditor, Iris, IrisAssessment } from "../Iris";

import genre_fit from "../../images/blog_genre_fit.png";
import adjectives from "../../images/adjectives.png";
import verbs from "../../images/verbs.png";
import mypoolitzer from "../../images/mypoolitzer.svg";
import test_image from "../../images/bs_age_groups.png";
import competition_man from "../../images/competition_man.png";
import dave from "../../images/dave.jpeg";
import lbf from "../../images/lbf.png";
import iris_editor from "../../images/iris_editor.png";
import { SignupButton } from "../SignupButton/SignupButton";

export interface Blog {
  created: Date;
  slug: string;
  title: string | JSX.Element;
  teaser: string | JSX.Element;
  teaserTitle?: string | JSX.Element;
  teaserImage?: string;
  contentImage?: string;
  content: JSX.Element;
}

export const TEST_BLOG: Blog = {
  created: new Date("2024-10-31"),
  slug: "test-blog",
  title: "This is test blog",
  teaser:
    "Does your writing style fit your audience? Is the pace of your story aligned with your readers' expectations? The answer to these questions lies in mastering the norms of your genre.",
  teaserImage: test_image,
  contentImage: genre_fit,
  content: (
    <>
      Does your writing style fit your audience? Is the pace of your story
      aligned with your readers' expectations? The answer to these questions
      lies in mastering the norms of your genre.
      <br />
      <br />
      Every genre has its unique set of conventions, and understanding them is
      crucial to crafting a story that resonates with readers. With <Iris />,
      authors gain access to genre-specific feedback, allowing them to tailor
      their stories to meet reader expectations while maintaining originality.
      <br />
      <br />
      Genres like <i>thrillers</i> demand a <b>fast pace</b>, with high-stakes
      action and tension throughout. In contrast, romance readers may expect
      slower, character-driven narratives with deeper emotional arcs. <Iris />{" "}
      provides data on how well your manuscript aligns with these genre-specific
      expectations, ensuring your story hits the right notes.
    </>
  ),
};

export const EDITOR_LAUNCH_BLOG: Blog = {
  created: new Date("2025-05-05"),
  slug: "editor-launch",
  title: (
    <>
      Product Launch: <IrisEditor />, our new real-time writing assistant
    </>
  ),
  teaserTitle: (
    <>
      Product Launch: <IrisEditor />
    </>
  ),
  teaser: (
    <>
      Meet <IrisEditor />, our new real-time writing assistant. It's like
      Grammarly, with additional metrics specifically for fiction authors.
    </>
  ),
  teaserImage: iris_editor,
  content: (
    <>
      Over the last two years, <Iris /> has assessed the works of over 1000
      authors and helped them improve their writing. Our users value her instant
      feedback, objectivity, and visual comparisons. However, we've also gotten
      feedback that the workflow - uploading a document and getting a report
      version by version - isn't dynamic enough when you're in the flow working
      on a manuscript.
      <br />
      <br />
      Based on this feedback, we've just released a new product: <IrisEditor />{" "}
      - our new real-time writing assistant.
      <IrisEditor /> gives you all the familiar reports you love from <Iris />,
      plus deep editorial hints - all in real-time while you type. It's like
      Grammarly, with additional metrics specifically for fiction authors.
      <br />
      <br />
      <p style={{ textAlign: "center" }}>
        <img src={iris_editor} style={{ width: "100%", maxWidth: 800 }} />
      </p>
      <br />
      <br />
      <IrisEditor /> offers:
      <ul>
        <li>
          A modern, browser-based editor to save your work in the cloud - secure
          and private
        </li>
        <li>
          All the familiar Quantifiction metrics - sales potential, predicted
          rating, genre fit, pace, active verbs and many more - shown to you in
          real time
        </li>
        <li>Histograms and genre comparisons</li>
        <li>
          Editorial feedback about clichés, inconsistencies, show don't tell,
          ...
        </li>
        <li>Various feedback highlighted directly in your text</li>
        <li>Export to Word and PDF</li>
      </ul>
      <br />
      <IrisEditor /> does not replace but complement the classic{" "}
      <IrisAssessment />, giving you full flexibility on what works best for
      you.
      <br />
      <br />
      <b>
        Sign up now and get <IrisEditor />7 days for free!
      </b>
      <br />
      <br />
      <SignupButton place="blog_launch" />
    </>
  ),
};

export const COMPETITION_WINNER_BLOG: Blog = {
  created: new Date("2025-04-23"),
  slug: "competition-winner",
  title: "Winner's Announcement for the AI-Judged Writing Competition",
  teaserTitle: "Winner's Announcement",
  teaser:
    "We are thrilled to announce the winner for this year's AI-Judged Writing Competition: Dave Vaughan.",
  teaserImage: dave,
  contentImage: dave,
  content: (
    <>
      We are thrilled to announce the winner of the First AI-Judged Writing
      Competition: <b>Dave Vaughan</b>'s{" "}
      <b>
        <i>Killing Them Again</i>
      </b>{" "}
      stood out among many talented entries, impressing both our AI judging
      system <Iris /> and our human panel with its originality, style, and
      potential.
      <br /> <br />
      In this dark satirical thriller, a missing girl, a crumbling town, and a
      journalist with a dangerous past collide in a twisted tale of power,
      deception, and revenge. As reporter Babe Lincoln digs into the
      disappearance of the acting mayor's daughter, she finds herself tangled in
      a web of grotesque secrets, corrupt officials, and surreal horrors that
      threaten to consume her. With a cast of eccentric outcasts and a parrot
      that sees everything, this darkly satirical thriller blurs the line
      between justice and spectacle.
      <br /> <br />
      We extend our heartfelt congratulations to Dave and all the{" "}
      <Link href="https://quantifiction.com/blog/competition-shortlist">
        shortlisted authors
      </Link>{" "}
      whose entries showcased exceptional creativity, craft, and bold
      imagination.
      <br /> <br />
      A sincere thank you goes out to everyone who submitted their stories - we
      received an overwhelmingly diverse number of entries from across the
      globe, spanning a rich tapestry of genres. Your work made this competition
      vibrant, fiercely competitive and incredibly inspiring.
      <br /> <br />
      The <b>First AI-Judged Writing Competition</b> was realized by{" "}
      <Link href="https://mypoolitzer.com/en">MyPoolitzer</Link>,{" "}
      <Link href="https://quantifiction.com">Quantifiction</Link> and{" "}
      <Link href="https://bluedenimpress.com">Blue Denim Press</Link> to usher
      in a new era of objectivity and consistency in evaluating literary works.
      The competition leverages MyPoolitzer's web-based platform in combination
      with Quantifiction's artificial intelligence (AI) to assess submissions
      across a wide range of objective criteria, complemented by a team of human
      judges.
      <br /> <br />
      This competition marks the beginning of an exciting era where human
      creativity meets AI innovation—and we can't wait to see what comes next.
      <br /> <br />
      Stay tuned for more.
    </>
  ),
};

export const COMPETITION_SHORTLIST_BLOG: Blog = {
  created: new Date("2025-04-06"),
  slug: "competition-shortlist",
  title: "Shortlisted Authors for the AI-Judged Writing Competition",
  teaserTitle: "Shortlist Announcement",
  teaser:
    "We are thrilled to announce the shortlisted authors for this year's AI-Judged Writing Competition.",
  teaserImage: competition_man,
  contentImage: competition_man,
  content: (
    <>
      We are thrilled to announce the shortlisted authors for our AI-Judged
      Writing Competition, a groundbreaking initiative that combines human
      creativity with the precision of artificial intelligence. This
      competition, in collaboration with{" "}
      <Link href="https://mypoolitzer.com/en/">MyPoolitzer</Link> and{" "}
      <Link href="https://bluedenimpress.com/">Blue Denim Press</Link>, has
      evaluated submissions based on sales potential, genre fit, and stylistic
      elements using Quantifiction's cutting-edge AI technology, Iris.
      <br />
      <br />
      <b>Shortlisted Authors</b>
      <ul>
        <li>Mouloud Benzadi</li>
        <li>Joseph J Duboiis</li>
        <li>Octavio Xavier</li>
        <li>Aaron Toronto</li>
        <li>Namita Joshi</li>
        <li>Stefan Sofiski</li>
        <li>John Fullerton</li>
        <li>Jessica Lease</li>
        <li>Adrian Bleese</li>
        <li>Dave Vaughan</li>
      </ul>{" "}
      These talented writers have demonstrated exceptional skill and creativity,
      and we are excited to see their full manuscripts as we move forward in the
      competition. Each shortlisted author will receive a comprehensive Iris
      Report, providing valuable insights into their work.
      <br />
      <br />
      We congratulate all shortlisted authors on this achievement and look
      forward to the next stage of the competition. The winner will receive a
      publishing contract with Blue Denim Press, marking a significant milestone
      in their literary journey.
      <br />
      <br />
      Stay tuned for further updates and announcements about the competition's
      progress.
    </>
  ),
};

export const EDITOR_BETA_BLOG: Blog = {
  created: new Date("2025-03-28"),
  slug: "editor-beta",
  title: (
    <>
      Beta Announcement: <IrisEditor />, our new real-time writing assistant
    </>
  ),
  teaserTitle: (
    <>
      Beta Announcement: <IrisEditor />
    </>
  ),
  teaser: (
    <>
      Meet <IrisEditor />, our new real-time writing assistant. It's like
      Grammarly, with additional metrics specifically for fiction authors.
    </>
  ),
  teaserImage: iris_editor,
  content: (
    <>
      Over the last two years, <Iris /> has assessed the works of over 1000
      authors and helped them improve their writing. Our users value her instant
      feedback, objectivity, and visual comparisons. However, we've also gotten
      feedback that the workflow - uploading a document and getting a report
      version by version - isn't dynamic enough when you're in the flow working
      on a manuscript.
      <br />
      <br />
      Based on this feedback, our engineering team has worked hard on a new
      product: <IrisEditor /> - our new real-time writing assistant.
      <IrisEditor /> gives you all the familiar reports you love from <Iris />,
      plus deep editorial hints - all in real-time while you type. It's like
      Grammarly, with additional metrics specifically for fiction authors.
      <br />
      <br />
      <p style={{ textAlign: "center" }}>
        <img src={iris_editor} style={{ width: "100%", maxWidth: 800 }} />
      </p>
      <br />
      <br />
      <IrisEditor /> offers:
      <ul>
        <li>
          A modern, browser-based editor to save your work in the cloud - secure
          and private
        </li>
        <li>
          All the familiar Quantifiction metrics - sales potential, predicted
          rating, genre fit, pace, active verbs and many more - shown to you in
          real time
        </li>
        <li>Histograms and genre comparisons</li>
        <li>
          Editorial feedback about clichés, inconsistencies, show don't tell,
          ...
        </li>
        <li>Various feedback highlighted directly in your text</li>
        <li>Export to Word and PDF</li>
      </ul>
      <br />
      <IrisEditor /> will not replace but complement the classic{" "}
      <IrisAssessment />, giving you full flexibility on what works best for
      you.
      <br />
      <br />
      We're rolling this product out gradually, starting now with a beta version
      for selected authors. If you're interested in trying it out ahead of the
      official launch <b>for free</b>, please reach out to{" "}
      <Link href="mailto:info@quantifiction.com">info@quantifiction.com</Link>.
      <br />
      <br />
      Once we conclude the beta program, we'll roll out <IrisEditor /> as a
      subscription-based service for everybody. Stay tuned 🙂
    </>
  ),
};

export const LBF_BLOG: Blog = {
  created: new Date("2025-02-02"),
  slug: "london-book-fair",
  title: "Meet Quantifiction at the 2025 London Book Fair",
  teaserTitle: "Meet Quantifiction at the 2025 LBF",
  teaser: (
    <>
      Join the Quantifiction team at the London Book Fair 2025 and how to
      transform your workflows with <Iris />, our groundbreaking AI platform.
    </>
  ),
  teaserImage: lbf,
  contentImage: lbf,
  content: (
    <>
      <Typography variant="h6" gutterBottom={true} style={{ fontWeight: 700 }}>
        To schedule a meeting with us at the London Book Fair: Click on our{" "}
        <Link href="https://calendly.com/quantifiction-info/london-book-fair-iris-demo">
          Calendly link
        </Link>{" "}
        to choose a meeting slot, or email us at{" "}
        <Link href="mailto:info@quantifiction.com">info@quantifiction.com</Link>
        .
      </Typography>
      Join the Quantifiction team at the{" "}
      <Link href="https://www.londonbookfair.co.uk/">
        London Book Fair 2025
      </Link>
      ! Discover how hundreds of authors, publishers, and agents are
      transforming their workflows with <Iris />, our groundbreaking AI
      platform.
      <br />
      <br />
      We'd love to show you our revolutionary assessment platform at the fair!
      It's powering an ongoing first-of-its-kind{" "}
      <Link href="https://mypoolitzer.com/en/competition/">
        writing contest
      </Link>{" "}
      in partnership with{" "}
      <Link href="https://mypoolitzer.com/en/">MyPoolitzer</Link> and{" "}
      <Link href="https://bluedenimpress.com/">Blue Denim Press</Link>, setting
      a new standard for literary evaluations. Don't miss it!
      <br />
      <br />
      <b>
        About <Iris />
      </b>
      <br />
      <Iris /> is a predictive AI-powered manuscript assessment tool that
      analyzes manuscripts in seconds, offering valuable insights into their
      true quality, potential weaknesses, and market appeal.
      <br />
      <br />
      <b>Why meet us?</b>
      <br />
      Discover how <Iris /> can revolutionize your publishing process by:
      <ul>
        <li>Instantly filtering your slush pile</li>
        <li>Identifying the next bestseller</li>
        <li>Offering in-depth genre comparisons</li>
        <li>Pinpointing genre fit and editorial strengths & weaknesses</li>
      </ul>
      <br />
      <b>🚀 Live Demos:</b> Experience <Iris />' full capabilities at our
      exclusive London Book Fair sessions
      <br />
      <b>📅 Book a Meeting:</b>{" "}
      <Link href="https://calendly.com/quantifiction-info/london-book-fair-iris-demo">
        Schedule on Calendly
      </Link>
      <br />
      <b>📧 Email:</b>{" "}
      <Link href="mailto:info@quantifiction.com">info@quantifiction.com</Link>
      <br />
      <br />
      We can't wait to connect and help take your publishing game to the next
      level!
    </>
  ),
};

export const COMPETITION_BLOG: Blog = {
  created: new Date("2025-01-30"),
  slug: "competition",
  title: "The World's First AI-Judged Literary Competition",
  teaserTitle: "First AI-Judged Literary Competition",
  teaser:
    "Join the world's first AI-judged literary competition hosted by Quantifiction, MyPoolitzer and Blue Denim Press for a chance to get published! ",
  teaserImage: competition_man,
  contentImage: competition_man,
  content: (
    <>
      <Link href="https://mypoolitzer.com/en/">MyPoolitzer</Link>,{" "}
      <Link href="https://quantifiction.com/">Quantifiction</Link>, and{" "}
      <Link href="https://bluedenimpress.com/">Blue Denim Press</Link> are
      thrilled to announce the launch of a groundbreaking literary competition
      that marks a significant milestone in the publishing industry. This
      first-of-its-kind writing contest will utilize Quantifiction's artificial
      intelligence (AI) for judging, ushering in a new era of objectivity and
      consistency in evaluating literary works.
      <br />
      <br />
      The competition leverages MyPoolitzer's web-based platform, which
      automatically processes manuscripts, checks all contributions for
      AI-generated content, and ranks them by quality and sales potential. This
      innovative approach ensures that all manuscript submissions are handled
      efficiently and securely throughout the contest process.
      <br />
      <br />
      <b>Competition Details</b>
      <ul>
        <li>
          <b>Submissions: </b>The contest is open to authors of unpublished,
          original adult fiction in English, with a maximum word count of
          80,000.
        </li>
        <li>
          <b>Entry Fee: </b>$30 USD, payable through a secure Stripe payment
          link.
        </li>
        <li>
          <b>Initial Submission: </b>Authors submit a synopsis and the first 20
          pages of their novel through the MyPoolitzer submission software.
        </li>
        <li>
          <b>AI Evaluation: </b>All entries will be analyzed using
          Quantifiction's cutting-edge AI technology, <Iris />, which employs a
          combination of Machine Learning and Natural Language Processing to
          assess manuscripts.
        </li>
        <li>
          <b>Judging Criteria: </b>
          <Iris /> will evaluate submissions based on sales potential, genre
          fit, and stylistic elements across over 30 dimensions, including
          reading difficulty, pacing, and emotional resonance.
        </li>
      </ul>
      <b>Timeline</b>
      <ul>
        <li>
          <b>February 1, 2025: </b>Competition opens for submissions
        </li>
        <li>
          <b>March 31, 2025: </b>Submission deadline
        </li>
        <li>
          <b>April 6, 2025: </b>Shortlist announcement
        </li>
        <li>
          <b>April 21, 2025: </b>Winner announcement
        </li>
      </ul>
      <b>Prize</b>
      <br />
      The winner of this innovative contest will be awarded a coveted book
      contract with Blue Denim Press, a renowned publisher of quality literary
      prose with global distribution. This presents an unparalleled opportunity
      for both emerging and established writers to gain recognition and reach a
      wider audience.
      <br />
      <br />
      Jost-Tilo Gehrke, Founder & CEO of MyPoolitzer, commented on the
      initiative: "AI strictly adheres to the contest brief, allowing for
      maximum objectivity, consistency and independence in the evaluation and
      decision-making process."
      <br />
      <br />
      This sentiment is echoed by Timo Bozsolik-Torres, Co-Founder & CTO of
      Quantifiction, who added: "Our analysis tool Iris not only provides
      ratings, but also gives decisive tips on how the author can improve their
      manuscript."
      <br />
      <br />
      Both MyPoolitzer and Quantifiction will showcase this writing competition
      at the upcoming London Book Fair from March 11 to March 13, 2025.
      <br />
      <br />
      While this AI-assisted judging process marks a revolutionary approach to
      literary contests, it is important to note that the final judging will
      involve both Iris AI and a panel of human judges, ensuring a balanced
      evaluation that combines technological innovation with human insight. This
      competition not only offers writers a chance at publication but also
      provides valuable feedback through AI-generated manuscript evaluations.
      All shortlisted candidates will receive a full AI evaluation report on
      their entire manuscript, offering unprecedented insights into their work.
      <br />
      <br />
      The organizers invite writers worldwide to participate in this exciting
      new chapter of literary discovery. For more information and to submit your
      work, please visit our competition website at{" "}
      <Link href="https://www.mypoolitzer.com/en/competition/">
        www.mypoolitzer.com/en/competition/
      </Link>
      .
    </>
  ),
};

export const MYPOOLITZER_BLOG: Blog = {
  created: new Date("2024-10-24"),
  slug: "mypoolitzer-partnership",
  title:
    "MyPoolitzer and Quantifiction Unveil Revolutionary AI-Assisted Submission Management Software",
  teaserTitle: "MyPoolitzer and Quantifiction",
  teaser:
    "MyPoolitzer, a leading name in submission management solutions, has joined forces with AI innovator Quantifiction to introduce a groundbreaking AI-driven analysis feature.",
  teaserImage: mypoolitzer,
  content: (
    <>
      MyPoolitzer, a leading name in submission management solutions, has joined
      forces with AI innovator Quantifiction to introduce a groundbreaking
      AI-driven analysis feature. This cutting-edge technology is poised to
      transform the manuscript evaluation process for agents and publishers,
      particularly in handling unsolicited submissions, often referred to as the
      "slush pile".
      <br />
      <br />
      The new feature leverages advanced cutting-edge LLM technology, combined
      with classical natural language processing, to automatically assess
      manuscript quality based on four critical parameters: style fit, genre
      fit, predicted rating and sales potential. These factors are synthesized
      into a comprehensive manuscript score ranging from 0 to 100, enabling
      efficient filtering and prioritization of the top 5% to 10% of
      submissions. This significantly reduces the time spent on initial
      evaluations, allowing industry professionals to focus on the most
      promising manuscripts.
      <br />
      <br />
      <b>Enhanced Efficiency and Fraud Prevention</b>
      <br />
      MyPoolitzer's software continues to incorporate its established AI content
      detection capabilities, ensuring all incoming texts are screened for
      potential AI-generated content. This dual approach of quality assessment
      and fraud prevention represents a significant advancement in submission
      management, offering unparalleled efficiency and accuracy.
      <br />
      <br />
      <b>Industry Impact and Future Prospects</b>
      <br />
      The collaboration between MyPoolitzer and Quantifiction marks a pivotal
      moment in the publishing industry, addressing long-standing challenges in
      manuscript evaluation. By streamlining the selection process, this tool
      empowers agents and publishers to discover high-potential works more
      efficiently. For small companies in particular, this increases the chances
      of identifying new voices from the slush pile and expanding the range of
      promising debut authors.
      <br />
      <br />
      <b>Frankfurt Book Fair Showcase</b>
      <br />
      MyPoolitzer is scheduled to demonstrate this innovative software at
      Frankfurt Book Fair this October. This presentation will offer industry
      professionals a first-hand look at how this tool can revolutionize
      manuscript evaluation approaches in publishing.
      <br />
      <br />
      <i>
        "Our partnership with Quantifiction has enabled us to create a tool that
        not only saves time but also enhances the quality of manuscript
        selection,"
      </i>{" "}
      Jost-Tilo Gehrke, Founder of MyPoolitzer.{" "}
      <i>
        "We're excited to showcase how this technology can transform the
        publishing landscape at the Frankfurt Book Fair."
      </i>
      <br />
      <br />
      <i>
        "Partnering with MyPoolitzer enables us to tap into a powerful
        submission management solution and integrate seemlessly into a process
        they're already used to. This allows us to focus on our core value
        proposition and create a fairer, more streamlined manuscript selection
        process at the same time"
      </i>
      , says Timo Bozsolik-Torres, CTO of Quantifiction.
      <br />
      <br />
      This collaboration between MyPoolitzer and Quantifiction represents a
      significant step forward in leveraging AI technology to support and
      enhance the creative processes in publishing, while maintaining the
      integrity of human-authored content.
      <br />
      <br />
      <br />
      <b>About MyPoolitzer</b>
      <br />
      MyPoolitzer is a Berlin-based company specializing in submission
      management software solutions. Committed to innovation, MyPoolitzer
      leverages advanced technologies to streamline and enhance the manuscript
      submission and evaluation process for agents and publishers worldwide.
      <br />
      <br />
      <b>About Quantifiction</b>
      <br />
      Quantifiction is a leading artificial intelligence company dedicated to
      developing manuscript evaluation technologies that help authors improve
      their work and enhance the decision-making processes of publishers and
      agents. By serving all parts of the publishing industry, Quantifiction's
      mission is to help produce better stories with science.
    </>
  ),
};

export const GENRE_BLOG: Blog = {
  created: new Date("2024-11-14"),
  slug: "genre-specific-storytelling",
  title: (
    <>
      Genre-Specific Storytelling with <Iris />
    </>
  ),
  teaser:
    "Does your writing style fit your audience? Is the pace of your story aligned with your readers' expectations? The answer to these questions lies in mastering the norms of your genre.",
  teaserImage: genre_fit,
  content: (
    <>
      Does your writing style fit your audience? Is the pace of your story
      aligned with your readers' expectations? The answer to these questions
      lies in mastering the norms of your genre.
      <br />
      <br />
      Every genre has its unique set of conventions, and understanding them is
      crucial to crafting a story that resonates with readers. With <Iris />,
      authors gain access to genre-specific feedback, allowing them to tailor
      their stories to meet reader expectations while maintaining originality.
      <br />
      <br />
      Genres like thrillers demand a fast pace, with high-stakes action and
      tension throughout. In contrast, romance readers may expect slower,
      character-driven narratives with deeper emotional arcs. <Iris /> provides
      data on how well your manuscript aligns with these genre-specific
      expectations, ensuring your story hits the right notes.
      <br />
      <br />
      Check out the <i>genre</i> tab of your report, it might look something
      like this:
      <Box my={2} textAlign="center">
        <img src={genre_fit} style={{ width: "100%", maxWidth: 800 }} />
      </Box>
      This report shows how much your manuscript reflects each genre, organized
      as a tree - you can expand sections and drill into sub-genres. <Iris />{" "}
      detects an overall score for each genre, plus the fit over the progression
      of the manuscript (darker colors mean a closer fit).
      <br />
      <br />
      You can use this information in two ways. First, you can assess whether
      your story aligns with the genre(s) you intended to write for. If you're
      off, you can either change your writing style, or pivot to another genre
      when you're pitching to publishers. Second, you can match the genre
      progression against your plot. For example, the manuscript above is a tech
      thriller, combining elements of science fiction with fast-paced action.
      You probably want certain parts of your manuscript to have more of a
      science fiction feel (i.e. exposition at the beginning) while others
      should align closer with elements of a thriller (i.e. the climax at the
      end).
      <br />
      <br />
      <Link href="https://quantifiction.com/reports">
        Read more about interpreting <Iris />' reports
      </Link>
      <br />
      <br />
      By aligning your story with genre conventions, you're more likely to keep
      readers engaged. <Iris /> helps ensure your plot, pacing, and character
      development are consistent with what readers expect, making your
      manuscript more appealing to potential publishers or agents looking for
      stories that fit neatly within a specific category.
      <br />
      <br />
      <Iris />
      's genre-specific feedback is invaluable for authors who want to ensure
      their stories don't stray too far from reader expectations while still
      offering room for creativity.
    </>
  ),
};

export const REVISION_BLOG: Blog = {
  created: new Date("2024-12-07"),
  slug: "revision",
  title: (
    <>
      Revisions with <Iris />
    </>
  ),
  teaser: (
    <>
      Much more than just a scorecard, <Iris /> offers insights that drive
      meaningful revisions, helping authors improve their stories while
      maintaining creative freedom.
    </>
  ),
  teaserImage: adjectives,
  content: (
    <>
      When writers think of data-driven, AI-supported technology, some might
      assume such tools are rigid or formulaic. That's not so with Iris'
      manuscript reports. Much more than just a scorecard, Iris offers insights
      that drive meaningful revisions, helping authors improve their stories
      while maintaining creative freedom.
      <br />
      <br />
      <b>Data as a Guide, Not a Rule</b>
      <br />
      <br />
      The beauty of <Iris /> lies in its ability to give feedback without
      dictating the terms. For instance, if <Iris /> flags a pacing issue, it's
      not suggesting you follow a fixed template - rather, it points to where
      the narrative might be losing momentum. But you, the author, decide
      whether that is so. You're in control. You interpret this feedback in
      various ways - maybe you cut a scene here, or introduce a subplot to
      maintain interest there.
      <br />
      <br />
      The key is that <Iris /> provides the data, but you decide on its
      relevance, and how to implement it, a flexibility that ensures that your
      voice and vision remain at the forefront, the data a supportive guide
      rather than a strict rulebook.
      <br />
      <br />
      Consider an author who discovers through <Iris /> their pacing is too slow
      for their genre. Armed with this feedback, the author first checks how the
      slowest chapters map to their story - even the fastest thriller needs a
      breather every now and then. Wherever they find discrepancy, they can work
      on adapting the pace to match their intention. The result? A clearer,
      faster-paced and more compelling read.
      <br />
      <br />
      In another case, an author might use <Iris /> to fine-tune the complexity
      of their writing. <Iris /> measures the reading score of their work and
      shows whether it's in line with readers' expectations for the genre.
      They'll streamline the language to resonate better with their audience.
      <br />
      <br />
      <b>Practical Tips for Revisions</b>
      <br />
      <ul>
        <li>
          <b>Focus on the Lows</b>: By bringing up the lows, consistency of your
          work increases. There's a definite correlation between consistency,
          overall quality, and ultimately the sales potential.
        </li>
        <li>
          <b>One Metric at a Time</b>: <Iris /> provides data across multiple
          areas, but it's important not to feel overwhelmed. Start by addressing
          the most pressing concerns—whether pacing, character development, or
          genre alignment—before moving on to other aspects.
        </li>
        <li>
          <b>Use Data to Support Intuition:</b> Writers often have an instinct
          about what's not working in their story. <Iris />' feedback can either
          confirm those hunches, reveal areas you might not have considered,
          help you make more informed revisions.
        </li>
        <li>
          <b>
            Improve Based on <Iris />' Feedback
          </b>
          : Writing is a process, and so is revision. After making changes based
          on <Iris />' insights, run the manuscript through the tool again to
          see how the revisions have impacted the overall score. Tip - many{" "}
          <Iris /> users run their manuscript 3, 4 or more times in pursuit of
          excellence.
        </li>
      </ul>
    </>
  ),
};

export const VERBS_BLOG: Blog = {
  created: new Date("2025-01-13"),
  slug: "verbs",
  title: <>Do you know your verbs?</>,
  teaser: (
    <>
      Do you know where all the action is in your manuscript? Verbs! Without
      these, your characters can't do anything, can't <i>feel</i> anything -
      can't even
      <i>be</i> anything.
    </>
  ),
  teaserImage: verbs,
  content: (
    <>
      Do you know where all the action is in your manuscript? Verbs! Without
      these, your characters can't do anything, can't <i>feel</i> anything -
      can't even
      <i>be</i> anything.
      <br />
      <br />
      Because verbs are so important, <Iris /> analyzes them in various ways,
      and you should, too. So let's{" "}
      <Link href="https://app.quantifiction.com/my_work">take a look</Link> at
      your <Iris /> report now, see if we can polish your work a little.
      <br />
      <br />
      Didn't upload anything yet? No worries, it's never too late to{" "}
      <Link href="https://app.quantifiction.com/new">get started</Link>.
      <br />
      <br />
      <b>Active Verbs</b> (Style → Active Verbs)
      <br />
      In fiction, the goal is clear: you should write as much as possible in
      active voice.{" "}
      <Link href="https://thenarrativearc.org/constance-hale">
        Strong active verbs improve your writing
      </Link>{" "}
      and{" "}
      <Link href="https://www.be-a-better-writer.com/passive-verbs.html">
        passive verbs should be replaced
      </Link>
      . <Iris /> measures the proportion of active vs. passive verbs in your
      writing.
      <br />
      <br />
      <b>Auxiliary Verbs</b> (Style → Auxiliary Verbs)
      <br />
      Also known as helping verbs, they support other verbs, often in forms such
      as be, had and do, typically leading to passive voice. But if your work
      relies too heavily on the "be" verb in fiction{" "}
      <Link href="https://www.writermag.com/improve-your-writing/revision-grammar/not-removing-verbs-writing/">
        you risk sounding vague or, worse, boring
      </Link>
      .{" "}
      <Link href="https://kathysteinemann.com/Musings/to-be/">
        Choosing a stronger main verb
      </Link>{" "}
      or rewriting the sentence in active voice strengthens your writing.
      <br />
      <br />
      <b>Continuous Verbs</b> (Style → Continuous Verbs)
      <br />
      These “-ing” verbs convey action or condition that is happening right now
      and may be ongoing. While they can add energy and help readers understand
      when the action is happening, they may weaken your writing by{" "}
      <Link href="https://ellenbrockediting.com/2013/02/05/stop-using-ing-verbs-in-your-novel/">
        reducing the reader's perception of immediacy
      </Link>
      .{" "}
      <Link href="https://kathysteinemann.com/Musings/to-be/">
        Minimizing use of these verbs
      </Link>{" "}
      can increase tension and improve flow.
      <br />
      <br />
      <b>Verb Distribution</b> (Words → Verbs)
      <br />
      <Iris /> visualizes the distribution of nouns, verbs, adjectives, adverbs
      and pronouns, and compares their frequency with the norms of your genre.
      Thrillers typically show a higher proportion of nouns and verbs, to convey
      fast-paced action, while other genres use more adjectives in their
      exposition. The key point is: orient your manuscript to your genre norms
      and you'll satisfy your readers' expectations.
      <br />
      <br />
      Work on your verbs and you'll improve other metrics, such as reading
      score, pace, emotion, and - the Holy Grail - genre fit and sales
      potential.
      <br />
      <br />
      So what are you waiting for?{" "}
      <Link href="https://app.quantifiction.com/new">
        Start revising with <Iris /> today
      </Link>
      !
    </>
  ),
};

export const BLOGS: Blog[] = [
  EDITOR_LAUNCH_BLOG,
  COMPETITION_WINNER_BLOG,
  COMPETITION_SHORTLIST_BLOG,
  EDITOR_BETA_BLOG,
  LBF_BLOG,
  COMPETITION_BLOG,
  VERBS_BLOG,
  REVISION_BLOG,
  GENRE_BLOG,
  MYPOOLITZER_BLOG,
];



// WEBPACK FOOTER //
// ./src/components/Blog/Blog.tsx